import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Activity, Route, BarChart3, Clock, UserCheck, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { PageHelpButton } from "@/components/PageHelpButton";
import { toast } from "sonner";

interface DashboardStats {
  total_users: number;
  users_exportable: number;
  users_not_exportable: number;
  users_with_responses: number;
  users_with_meteo: number;
  active_users_7d: number;
  activities_completed: number;
  avg_activities_per_user: number;
  avg_session_minutes: number | null;
  path_completion: {
    started: number;
    completed: number;
    rate: number;
  };
  onboarding_completion: {
    assigned: number;
    started: number;
    completed: number;
    rate_vs_started: number;
    rate_vs_assigned: number;
  };
}

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/dashboard-stats`,
          {
            headers: {
              Authorization: `Bearer ${session?.access_token}`,
              apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            },
          }
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        setStats(data);
      } catch (e: any) {
        toast.error(e.message || "Erreur lors du chargement des statistiques");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="animate-spin h-8 w-8 text-muted-foreground" />
      </div>
    );
  }

  const exportableCount = stats?.users_exportable;
  const notExportableCount = stats?.users_not_exportable;
  const withResponsesCount = stats?.users_with_responses;
  const hasExportBreakdown =
    typeof exportableCount === "number" &&
    typeof notExportableCount === "number" &&
    typeof withResponsesCount === "number";

  const kpis = [
    {
      title: "Total utilisateurs",
      value: stats?.total_users ?? "—",
      icon: Users,
      description: hasExportBreakdown
        ? `Comptes inscrits (hors admins et testeurs) · ${notExportableCount} non éligible${notExportableCount > 1 ? "s" : ""} (< 18 ans et/ou CGU/PC non acceptées)`
        : "Comptes inscrits (hors admins et testeurs)",
      detail: hasExportBreakdown
        ? `${exportableCount} éligible${exportableCount > 1 ? "s" : ""} · ${withResponsesCount} participant${withResponsesCount > 1 ? "s" : ""} dans l'export CSV`
        : undefined,
    },
    {
      title: "Actifs (7 jours)",
      value: stats?.active_users_7d ?? "—",
      icon: UserCheck,
      description: "Connexion dans les 7 derniers jours",
    },
    {
      title: "Activités complétées",
      value: stats
        ? `${stats.activities_completed} (moy. ${stats.avg_activities_per_user}/user)`
        : "—",
      icon: Activity,
      description: "Total et moyenne par utilisateur",
    },
    {
      title: "Temps moyen de session",
      value: stats?.avg_session_minutes != null ? `${stats.avg_session_minutes} min` : "—",
      icon: Clock,
      description: stats?.avg_session_minutes == null ? "Données non disponibles" : "Durée moyenne par session",
    },
    {
      title: "Complétion parcours",
      value: stats ? `${stats.path_completion.rate} %` : "—",
      icon: Route,
      description: stats
        ? `${stats.path_completion.completed} terminés / ${stats.path_completion.started} commencés`
        : "Parcours de type standard",
    },
    {
      title: "Complétion onboarding",
      value: stats ? `${stats.onboarding_completion.rate_vs_assigned} %` : "—",
      icon: BarChart3,
      description: stats
        ? `${stats.onboarding_completion.completed} terminés / ${stats.onboarding_completion.started} commencés / ${stats.onboarding_completion.assigned} assignés`
        : "Parcours d'onboarding",
    },
  ];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" data-tour="dashboard-title" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Dashboard
          </h1>
          <p className="mt-1 text-muted-foreground">Vue d'ensemble de MindsUp</p>
        </div>
        <PageHelpButton tourKey="dashboard" />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-tour="dashboard-kpis">
        {kpis.map((kpi) => (
          <Card key={kpi.title} className="border-border/50">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                {kpi.title}
              </CardTitle>
              <kpi.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{kpi.value}</div>
              <p className="text-xs text-muted-foreground mt-1">{kpi.description}</p>
              {"detail" in kpi && kpi.detail ? (
                <p className="text-xs text-muted-foreground mt-1">{kpi.detail}</p>
              ) : null}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
